import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectProps,
  TextField,
} from '@mui/material';
import { ReactNode, useCallback, useRef, useState } from 'react';

export interface SearchableOption {
  value: string;
  label: string;
  disabled?: boolean;
  renderContent?: ReactNode;
}

type SearchableSelectProps = Omit<SelectProps<string | string[]>, 'children'> & {
  options: SearchableOption[];
};

const SearchableSelect = ({
  options,
  onChange,
  multiple,
  onOpen: onOpenProp,
  onClose: onCloseProp,
  MenuProps: menuPropsProp,
  ...selectProps
}: SearchableSelectProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  const handleEntered = useCallback(() => {
    if (paperRef.current) {
      paperRef.current.style.minWidth = `${paperRef.current.offsetWidth}px`;
    }
    searchInputRef.current?.focus();
    setSearchFocused(true);
  }, []);

  const filteredOptions = searchTerm.trim()
    ? options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  const handleOpen = (event: React.SyntheticEvent) => {
    setOpen(true);
    onOpenProp?.(event);
  };

  const handleClose = (event: React.SyntheticEvent) => {
    setOpen(false);
    setSearchTerm('');
    setSearchFocused(false);
    if (paperRef.current) {
      paperRef.current.style.minWidth = '';
    }
    onCloseProp?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (searchFocused && event.key !== 'Escape') {
      event.stopPropagation();
    }
  };

  const menuProps = {
    ...menuPropsProp,
    PaperProps: {
      ...menuPropsProp?.PaperProps,
      ref: paperRef,
      sx: {
        maxHeight: 350,
        ...((menuPropsProp?.PaperProps as any)?.sx || {}),
      },
    },
    TransitionProps: {
      ...menuPropsProp?.TransitionProps,
      onEntered: handleEntered,
    },
    disableAutoFocusItem: true,
    autoFocus: false,
  };

  return (
    <Select
      {...selectProps}
      multiple={multiple}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      onChange={onChange}
      MenuProps={menuProps}
    >
      <Box
        component="li"
        sx={{ p: 0, position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}
        onKeyDown={handleKeyDown}
      >
        <ClickAwayListener onClickAway={() => setSearchFocused(false)}>
          <TextField
            inputRef={searchInputRef}
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onClick={e => {
              e.stopPropagation();
              setSearchFocused(true);
            }}
            onKeyDown={handleKeyDown}
            fullWidth
            variant="outlined"
            sx={{ m: 1, width: 'calc(100% - 16px)' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      setSearchTerm('');
                      searchInputRef.current?.focus();
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </ClickAwayListener>
      </Box>
      {filteredOptions.length === 0 ? (
        <MenuItem disabled>No options found</MenuItem>
      ) : (
        filteredOptions.map(opt => (
          <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.renderContent ?? opt.label}
          </MenuItem>
        ))
      )}
    </Select>
  );
};

export default SearchableSelect;
